// Client-side mirror of dprEngine.js's computeCapacityConsumption's
// by-products math + computeSales, trimmed to what the wizard's live "Sales
// Estimation" preview needs. Kept here rather than imported from ca-backend
// since CRA's webpack build can't reach outside src/ — same pattern as
// dprLoanCalc.js / dprRmProcurementCalc.js.
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

// Year 1 is the actual/base figure — escalation compounds starting Year 2.
function escalatedValue(base, flatPct, yearIndex) {
  let value = base;
  for (let y = 2; y <= yearIndex; y++) value = value * (1 + (flatPct || 0) / 100);
  return value;
}

// Returns one row per projection year: { year, utilPct, monthsThisYear,
// totalSalesValue, products: [{ name, kind, unit, productionQty,
// openingStockQty, closingStockQty, netSalesQty, pricePerUnit, salesValue }] }
// — Main Product first, then each By-Product — matching dprEngine.js's
// computeSales exactly, so this preview matches the saved report once the
// case is recomputed server-side.
export function computeSalesEstimatePreview(caseData) {
  const cap = caseData.capacity_config || {};
  const rm = caseData.raw_material || {};
  const s = caseData.sales_config || {};
  const projectionYears = caseData.projection_years || 6;

  const rampUp = (cap.rampUpPctByYear && cap.rampUpPctByYear.length)
    ? cap.rampUpPctByYear
    : Array.from({ length: projectionYears }, (_, i) => Math.min(30 + i * 5, 100));

  const installedPerHour = cap.installedCapacityPerHour || 0;
  const hoursPerDay = (cap.hoursPerDay || 8) * (cap.shiftsPerDay || 1);
  const workingDays = cap.workingDaysPerYear || 300;
  const wastagePct = cap.wastagePct || 0;
  const installedAnnualQty = installedPerHour * hoursPerDay * workingDays * (1 - wastagePct / 100);

  const recipeLines = rm.recipeLines || [];
  const normalOperatingMonths = cap.normalOperatingMonths || 12;
  const operatingMonths = cap.operatingMonthsByYear || [];

  const allOutputRows = (rm.outputs || []).filter(o => o.name);
  const byProductCfgs = allOutputRows.filter(o => (o.kind || 'by_product') === 'by_product');
  const mainOutputRow = allOutputRows.find(o => o.kind === 'main');

  // Main Product's own row on the Products & By-Products table is the
  // primary source for its price/escalation/stock-days — sales_config is
  // only the fallback for a legacy case with no outputs table.
  const closingStockDays = mainOutputRow?.closingStockDays ?? (s.closingStockDays ?? 15);
  const yieldEntered = (o) => o.yieldPerRmUnit !== undefined && o.yieldPerRmUnit !== null && o.yieldPerRmUnit !== '';

  let prevMainClosing = 0;
  const bpPrevClosing = byProductCfgs.map(() => 0);

  return Array.from({ length: projectionYears }, (_, idx) => {
    const y = idx + 1;
    const utilPct = rampUp[idx] !== undefined ? rampUp[idx] : rampUp[rampUp.length - 1];
    const monthsThisYear = operatingMonths[idx] !== undefined ? operatingMonths[idx] : normalOperatingMonths;
    const productionQty = installedAnnualQty * (utilPct / 100) * (monthsThisYear / normalOperatingMonths);

    const lineQtyByName = {};
    recipeLines.forEach(line => { lineQtyByName[line.name] = (line.qtyPerOutputUnit || 0) * productionQty; });

    // Main Product
    const mainOpening = prevMainClosing;
    const mainClosing = round2(productionQty * (closingStockDays / 365));
    const mainNetSales = round2(mainOpening + productionQty - mainClosing);
    const mainPrice = escalatedValue(mainOutputRow?.pricePerUnit ?? (s.pricePerUnit || 0), mainOutputRow?.escalationPct ?? s.escalationPct, y);
    const mainSalesValue = round2(mainNetSales * mainPrice);
    prevMainClosing = mainClosing;

    const products = [{
      name: mainOutputRow?.name || 'Main Product', kind: 'main', unit: cap.capacityUnit || '',
      productionQty: round2(productionQty), openingStockQty: round2(mainOpening), closingStockQty: mainClosing,
      netSalesQty: mainNetSales, pricePerUnit: round2(mainPrice), salesValue: mainSalesValue,
    }];

    // By-Products — same Auto (Remainder) / Manual yield logic as the
    // Products & By-Products table on the Capacity & Raw Material step.
    byProductCfgs.forEach((bp, bi) => {
      const rmName = (bp.ingredients || [])[0];
      const rmQty = lineQtyByName[rmName] || 0;
      let yieldVal = bp.yieldPerRmUnit;
      if (!yieldEntered(bp)) {
        const otherYields = allOutputRows
          .filter(o => o !== bp && (o.ingredients || [])[0] === rmName && yieldEntered(o))
          .reduce((sum, o) => sum + Number(o.yieldPerRmUnit || 0), 0);
        yieldVal = Math.max(1 - otherYields, 0);
      }
      const bpProductionQty = round2(rmQty * (yieldVal || 0));

      const prevClosing = bpPrevClosing[bi];
      const bpDays = bp.closingStockDays ?? 15;
      const bpClosing = round2(bpProductionQty * (bpDays / 365));
      const bpNetSales = round2(prevClosing + bpProductionQty - bpClosing);
      const bpPrice = escalatedValue(bp.pricePerUnit || 0, bp.escalationPct, y);
      const bpSalesValue = round2(bpNetSales * bpPrice);
      bpPrevClosing[bi] = bpClosing;

      products.push({
        name: bp.name, kind: 'by_product', unit: bp.unit || '',
        productionQty: bpProductionQty, openingStockQty: round2(prevClosing), closingStockQty: bpClosing,
        netSalesQty: bpNetSales, pricePerUnit: round2(bpPrice), salesValue: bpSalesValue,
      });
    });

    return {
      year: y, utilPct, monthsThisYear,
      totalSalesValue: round2(products.reduce((sum, p) => sum + p.salesValue, 0)),
      products,
    };
  });
}
